import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import app from '../../services/firebase';
import TopNavbar from '../TopNavbar/TopNavbar';
import ErrorBubble from '../ErrorBubble/ErrorBubble'; // Import the ErrorBubble component

const ProfilePage = () => {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [weight, setWeight] = useState('');
  const [totalCaloriesBurned, setTotalCaloriesBurned] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const auth = getAuth(app);
  const db = getFirestore(app);

  useEffect(() => {
    const fetchProfile = async () => {
      const user = auth.currentUser;

      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUsername(data.username || '');
            setName(data.name || '');
            setWeight(data.weight || '');
            setTotalCaloriesBurned(data.totalCaloriesBurned || 0);
          }
        } catch (err) {
          console.error('Error fetching profile:', err);
          setError('Failed to fetch profile data. Please try again.');
        }
      }
    };

    fetchProfile();
  }, [auth, db]);

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    const user = auth.currentUser;
    if (!user) {
      setError('You must be logged in to update your profile.');
      return;
    }

    // Validation: Ensure the username is not empty
    if (!username.trim()) {
      setError('Username is required.');
      return;
    }

    try {
      await setDoc(
        doc(db, 'users', user.uid),
        { username, name, weight }, // Save username, name, and weight
        { merge: true } // Merge to avoid overwriting other fields
      );
      setSuccessMessage('Profile updated successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile data. Please try again.');
    }
  };

  const handleRefreshCalories = async () => {
    setError(null);
    setSuccessMessage(null);

    const user = auth.currentUser;
    if (!user) {
      setError('You must be logged in to refresh your calories.');
      return;
    }

    try {
      const dataCollectionRef = collection(db, `users/${user.uid}/data`);
      const dataDocs = await getDocs(dataCollectionRef);

      let totalCalories = 0;
      dataDocs.forEach((doc) => {
        const data = doc.data();
        totalCalories += data.caloriesBurned || 0; // Sum up the caloriesBurned values
      });

      // Update the totalCaloriesBurned field in the user's document
      await setDoc(
        doc(db, 'users', user.uid),
        { totalCaloriesBurned: totalCalories }, // Save as a number
        { merge: true }
      );

      setTotalCaloriesBurned(totalCalories);
      setSuccessMessage('Calories refreshed successfully!');
    } catch (err) {
      console.error('Error refreshing calories:', err);
      setError('Failed to refresh calories. Please try again.');
    }
  };

  return (
    <div>
      <TopNavbar />
      <div className="page-container">
        <h1>Profile</h1>
        {/* Use ErrorBubble for error and success messages */}
        <ErrorBubble message={error} type="error" />
        <ErrorBubble message={successMessage} type="success" />
        <form className="profile-form">
          <div>
            <label htmlFor="username">
              Username <span className="required-asterisk">*</span>
            </label>
            <input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="weight">Weight (lbs)</label> {/* Updated label */}
            <input
              id="weight"
              type="number"
              placeholder="Enter your weight"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === 'e' || // Prevent 'e'
                  e.key === 'E' || // Prevent 'E'
                  e.key === '+' || // Prevent '+'
                  e.key === '-' || // Prevent '-'
                  e.key === '.' || // Prevent '.' (if decimals are not allowed)
                  e.key === ','    // Prevent ',' (if commas are not allowed)
                ) {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div>
            <label htmlFor="totalCaloriesBurned">Total Calories Burned</label>
            <div className="input-container">
              <input
                id="totalCaloriesBurned"
                type="text"
                value={totalCaloriesBurned !== null && totalCaloriesBurned !== undefined ? totalCaloriesBurned : ''}
                placeholder="Loading..."
                readOnly
              />
              <button
                type="button"
                onClick={handleRefreshCalories}
                className="refresh-button"
                title="Refresh"
              >
                <i className="fas fa-redo refresh-icon"></i> {/* Circular arrow icon */}
              </button>
            </div>
          </div>
          <button type="button" onClick={handleSave}>
            Save
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;